/**
 * Patient Context Bar API
 *
 * Module-level patient context (medications, diagnoses, allergies, last
 * encounter) still rides v1 module-data endpoints. Treatment plan has
 * migrated to v2 REST (see `/v2/provider/patients/:patientId/treatment-plan`)
 * and consumes the generated hooks with cache-tag enhancements wired here.
 */

import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { baseApi } from '@store/baseApi';
import type { TreatmentPlan, TreatmentPlanResponse } from '@store/generated/patients';
import { enhancedApi as patientsGeneratedApi } from '@store/generated/patients';

import type {
  PatientModuleDataResponse,
  PatientPreRecordingInfoResponse,
} from '../types';

const PATIENT_CONTEXT_TAG_TYPES = [
  'PatientContext',
  'PatientModuleData',
  'PatientPreRecordingInfo',
  'PatientTreatmentPlan',
] as const;

/**
 * Patient Context Bar API — v1 module-data + pre-recording endpoints
 */
export const patientContextBarApi = baseApi
  .enhanceEndpoints({ addTagTypes: PATIENT_CONTEXT_TAG_TYPES })
  .injectEndpoints({
    endpoints: builder => ({
      getPatientModuleData: builder.query<PatientModuleDataResponse, { patientId: string }>({
        query: ({ patientId }) => ({
          url: `/patient/getPatientData?patient_id=${patientId}`,
          method: 'GET',
        }),
        providesTags: (_result, _error, { patientId }) => [
          { type: 'PatientModuleData', id: patientId },
          { type: 'PatientContext', id: patientId },
        ],
      }),

      getPatientPreRecordingInfo: builder.query<
        PatientPreRecordingInfoResponse,
        { patientId: string }
      >({
        query: ({ patientId }) => ({
          url: `/patient/getPreRecordingInformation?patient_id=${patientId}`,
          method: 'GET',
        }),
        providesTags: (_result, _error, { patientId }) => [
          { type: 'PatientPreRecordingInfo', id: patientId },
          { type: 'PatientContext', id: patientId },
        ],
      }),

      updatePatientModule: builder.mutation<
        undefined,
        {
          patientId: string;
          patientModuleId: string;
          dataKey: string;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          data: any[];
        }
      >({
        query: ({ patientId, patientModuleId, dataKey, data }) => ({
          url: '/patient/updatePatientModule',
          method: 'POST',
          body: {
            patient_id: patientId,
            patient_module_id: patientModuleId,
            data: { [dataKey]: data },
          },
        }),
        invalidatesTags: (_result, _error, { patientId }) => [
          { type: 'PatientModuleData', id: patientId },
          { type: 'PatientContext', id: patientId },
        ],
      }),
    }),
    overrideExisting: false,
  });

/**
 * Treatment Plan API — v2 REST, enhanced from generated endpoints
 *
 * Read: GET provides PatientTreatmentPlan tag
 * Writes (create/update/from-note/mark-reviewed/set-review-date/rollback)
 *   all invalidate the same tag, so every surface refetches.
 */
export const treatmentPlanApi = patientsGeneratedApi.enhanceEndpoints({
  addTagTypes: ['PatientTreatmentPlan'],
  endpoints: {
    getV2ProviderPatientsByPatientIdTreatmentPlan: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientTreatmentPlan' as const, id: patientId },
      ],
    },
    postV2ProviderPatientsByPatientIdTreatmentPlan: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientTreatmentPlan' as const, id: patientId },
      ],
    },
    patchV2ProviderPatientsByPatientIdTreatmentPlan: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientTreatmentPlan' as const, id: patientId },
      ],
    },
    postV2ProviderPatientsByPatientIdTreatmentPlanFromNote: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientTreatmentPlan' as const, id: patientId },
      ],
    },
    postV2ProviderPatientsByPatientIdTreatmentPlanMarkReviewed: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientTreatmentPlan' as const, id: patientId },
      ],
    },
    postV2ProviderPatientsByPatientIdTreatmentPlanReviewDate: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientTreatmentPlan' as const, id: patientId },
      ],
    },
    postV2ProviderPatientsByPatientIdTreatmentPlanRollback: {
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'PatientTreatmentPlan' as const, id: patientId },
      ],
    },
  },
});

// =============================================================================
// Legacy v1 hooks (module data + pre-recording info)
// =============================================================================
export const {
  useGetPatientModuleDataQuery,
  useLazyGetPatientModuleDataQuery,
  useGetPatientPreRecordingInfoQuery,
  useLazyGetPatientPreRecordingInfoQuery,
  useUpdatePatientModuleMutation,
} = patientContextBarApi;

// =============================================================================
// Treatment plan hooks — user-friendly names over generated v2 endpoints
// =============================================================================
export const useGetTreatmentPlanQueryRaw =
  treatmentPlanApi.useGetV2ProviderPatientsByPatientIdTreatmentPlanQuery;
export const useCreateTreatmentPlanMutation =
  treatmentPlanApi.usePostV2ProviderPatientsByPatientIdTreatmentPlanMutation;
export const useUpdateTreatmentPlanMutation =
  treatmentPlanApi.usePatchV2ProviderPatientsByPatientIdTreatmentPlanMutation;
export const useSaveNoteAsTreatmentPlanMutation =
  treatmentPlanApi.usePostV2ProviderPatientsByPatientIdTreatmentPlanFromNoteMutation;
export const useMarkTreatmentPlanReviewedMutation =
  treatmentPlanApi.usePostV2ProviderPatientsByPatientIdTreatmentPlanMarkReviewedMutation;
export const useSetTreatmentPlanReviewDateMutation =
  treatmentPlanApi.usePostV2ProviderPatientsByPatientIdTreatmentPlanReviewDateMutation;
export const useRollbackTreatmentPlanMutation =
  treatmentPlanApi.usePostV2ProviderPatientsByPatientIdTreatmentPlanRollbackMutation;

type TreatmentPlanQueryError = FetchBaseQueryError | SerializedError | undefined;

/**
 * A patient with no plan yet is the normal "empty" path. The backend now returns
 * 204 No Content for it, which RTK Query surfaces as a success with empty `data`
 * (unwrapped to `null` below) — so it never reaches this helper. We still treat a
 * 404 as "no plan" to stay correct during deploy skew, while the backend rolls
 * out the 204 (GDN-1684). Once fully deployed, this branch is dormant.
 */
function isNotFound(err: TreatmentPlanQueryError): boolean {
  return !!err && 'status' in err && err.status === 404;
}

type RawTreatmentPlanQueryResult = ReturnType<typeof useGetTreatmentPlanQueryRaw>;

/**
 * Return shape: the raw RTK hook result with `data` and `error` replaced
 * by the unwrapped / 404-normalized values. `Omit` distributes across the
 * hook's discriminated return union so every status field (isLoading,
 * isFetching, isSuccess, isError, refetch, currentData, etc.) survives.
 */
export type UseTreatmentPlanQueryResult = Omit<RawTreatmentPlanQueryResult, 'data' | 'error'> & {
  data: TreatmentPlan | null;
  error: TreatmentPlanQueryError;
};

/**
 * Fetch a patient's treatment plan and unwrap the `{ treatment_plan }` envelope.
 * Returns `null` when the patient has no plan yet — a 204 No Content (or a 404
 * from an older backend during deploy skew) — so callers can branch on "no plan"
 * without handling an error state.
 *
 * Pass `{ skip: true }` when the patientId isn't known yet.
 */
export function useGetTreatmentPlanQuery(
  arg: { patientId: string },
  options?: Parameters<typeof useGetTreatmentPlanQueryRaw>[1],
): UseTreatmentPlanQueryResult {
  const result = useGetTreatmentPlanQueryRaw(arg, options);
  // The chained enhanceEndpoints narrows these to `any` — re-assert via
  // the generated response + RTK's error types so downstream access is
  // type-safe (`data.treatment_plan`, `'status' in error`).
  const data = result.data as TreatmentPlanResponse | undefined;
  const error = result.error as TreatmentPlanQueryError;
  const is404 = isNotFound(error);

  // Spreading a union loses the status fields (TS narrows to the common
  // subset), so we reconstitute the widened shape via the return-type
  // annotation. The cast is honest — `result` at runtime has every field
  // from the raw hook; we're only swapping two typed projections.
  return {
    ...result,
    data: is404 ? null : (data?.treatment_plan ?? null),
    error: is404 ? undefined : error,
  } as UseTreatmentPlanQueryResult;
}
